"use client";

import { Empty, Modal, Spin } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IconCalendarMonth,
  IconChevronLeft,
  IconChevronRight,
} from "@/lib/icons";
import styles from "./SlotStep.module.css";
import { ENDPOINTS } from "@/constants/endpoint";
import { DEV_AUTH_HEADERS } from "@/lib/devHeaders";
import { formatEventTime } from "@/lib/formatDateTime";
import { getNextStep } from "./stepFlow";

// Date strip: how many days ahead it covers (the API caps a range at 62).
const STRIP_DAYS = 60;
// Calendar view: weeks on screen, weeks per arrow click, how far ahead
const GRID_WEEKS = 6;
const STEP_WEEKS = 4;
const MONTHS_AHEAD = 12;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Day status from the calendar API → label shown under the date
const STATUS_LABEL = {
  limited: "Few left",
  sold_out: "Sold out",
  unavailable: "Unavailable",

};

const fetchJson = async (path) => {
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/${path}`, {
    credentials: "include",
    cache: "no-store",
    headers: { ...DEV_AUTH_HEADERS },
  });
  return res.json();
};

const isBookable = (day) =>
  !!day && (day.status === "open" || day.status === "limited");

const mondayOf = (d) => d.startOf("day").subtract((d.day() + 6) % 7, "day");

/**
 * Date + time slot picker for recurring shows (parks, gaming zones,
 * museums). The customer picks a day in a calendar (or a list), then an
 * entry slot. Availability comes from the show's schedule; the chosen slot's
 * start goes into the order as slot_start_at, and that day's ticket prices
 * replace the default ones.
 */
const SlotStep = ({
  showId,
  baseTickets,
  orderDetails,
  setOrderDetails,
  setTickets,
  setActiveStep,
  availableSteps,
  /** Event/show start date (YYYY-MM-DD) — jump strip/calendar here for far-future shows */
  showStartDate,
}) => {
  const today = useMemo(() => dayjs().startOf("day"), []);
  // the full month calendar opens in a dialog; the strip is the default
  const [calendarOpen, setCalendarOpen] = useState(false);
  const stripRef = useRef(null);
  // First week on screen: never before the current week, so the calendar
  // doesn't open on a month of past days
  const firstWeek = useMemo(() => mondayOf(today), [today]);
  const earliestShowDay = useMemo(() => {
    if (!showStartDate) return null;
    const start = dayjs(showStartDate).startOf("day");
    return start.isValid() && start.isAfter(today) ? start : null;
  }, [showStartDate, today]);
  // Strip starts at the show's first day when that's still in the future
  const stripAnchor = earliestShowDay || today;
  const [weekStart, setWeekStart] = useState(() => {
    if (orderDetails?.slot?.date) {
      const picked = mondayOf(dayjs(orderDetails.slot.date));
      return picked.isBefore(firstWeek) ? firstWeek : picked;
    }
    if (earliestShowDay) return mondayOf(earliestShowDay);
    return firstWeek;
  });
  const gridEnd = weekStart.add(GRID_WEEKS * 7 - 1, "day");
  const lastWeek = mondayOf(
    (earliestShowDay || today).add(MONTHS_AHEAD, "month"),
  );
  // Day statuses by date, from every range fetched so far
  const [days, setDays] = useState({});
  const [loadingDays, setLoadingDays] = useState(true);
  const [date, setDate] = useState(orderDetails?.slot?.date || null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState(null);
  // Prices are re-applied per day from the API; keep the original list so
  // writing day prices back into the tickets state doesn't refetch.
  const baseTicketsRef = useRef(baseTickets);
  const fetched = useRef(new Set());

  /** Load day statuses for a date range once. */
  const loadRange = useCallback(
    async (from, to) => {
      const key = `${from}_${to}`;
      if (!showId || fetched.current.has(key)) return null;
      fetched.current.add(key);
      setLoadingDays(true);
      try {
        const res = await fetchJson(
          `${ENDPOINTS.SHOW_CALENDAR.replace(":showId", showId)}?from=${from}&to=${to}`,
        );
        if (!res?.status) {
          setError(res?.message || "Couldn't load dates.");
          return [];
        }
        const list = res.data?.days || [];
        setDays((prev) => ({
          ...prev,
          ...Object.fromEntries(list.map((d) => [d.date, d])),
        }));
        return list;
      } catch {
        setError("Couldn't load dates.");
        return [];
      } finally {
        setLoadingDays(false);
      }
    },
    [showId],
  );

  // The date strip: 60 days from today, or from the show's first open day
  // when the season starts far in the future (BUG-W2-05).
  useEffect(() => {
    loadRange(
      stripAnchor.format("YYYY-MM-DD"),
      stripAnchor.add(STRIP_DAYS - 1, "day").format("YYYY-MM-DD"),
    ).then((list) => {
      // Open on the first day that can be booked
      if (list?.length) {
        const first = list.find(isBookable);
        if (first?.date) {
          setDate((current) => current || first.date);
          const week = mondayOf(dayjs(first.date));
          setWeekStart((current) =>
            current.isSame(firstWeek) && week.isAfter(firstWeek)
              ? week
              : current,
          );
        }
      }
    });
  }, [stripAnchor, loadRange, firstWeek]);

  // The weeks shown in the calendar dialog
  useEffect(() => {
    if (!calendarOpen) return;
    const from = weekStart.isAfter(today) ? weekStart : today;
    loadRange(
      from.format("YYYY-MM-DD"),
      weekStart.add(GRID_WEEKS * 7 - 1, "day").format("YYYY-MM-DD"),
    );
  }, [calendarOpen, weekStart, today, loadRange]);

  // The details panel shows the chosen day before a slot is picked
  useEffect(() => {
    if (!date) return;
    setOrderDetails((prev) =>
      prev?.pending_date === date ? prev : { ...(prev || {}), pending_date: date },
    );
    // keep the chosen day in view in the strip
    requestAnimationFrame(() => {
      stripRef.current
        ?.querySelector(`[data-date="${date}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    });
  }, [date, setOrderDetails]);

  // Slots and that day's prices for the chosen date.
  useEffect(() => {
    if (!showId || !date) return;
    let active = true;

    setLoadingSlots(true);
    fetchJson(`${ENDPOINTS.SHOW_SLOTS.replace(":showId", showId)}?date=${date}`)
      .then((res) => {
        if (!active) return;
        if (!res?.status) {
          setSlots([]);
          setError(res?.message || "Couldn't load time slots.");
          return;
        }
        setError(null);
        setSlots(res.data?.slots || []);

        // Same tickets, with the price that applies on this day.
        const dayPrices = Object.fromEntries(
          (res.data?.tickets || []).map((t) => [String(t.id), t.price]),
        );
        const base = baseTicketsRef.current;
        if (Array.isArray(base) && base.length > 0) {
          setTickets?.(
            base.map((t) =>
              dayPrices[String(t.id)] != null
                ? { ...t, price: dayPrices[String(t.id)] }
                : t,
            ),
          );
        }
      })
      .catch(() => active && setError("Couldn't load time slots."))
      .finally(() => active && setLoadingSlots(false));

    return () => {
      active = false;
    };
  }, [showId, date, setTickets]);

  const chooseDate = (value) => {
    setDate(value);
    setOrderDetails((prev) =>
      prev?.slot ? { ...prev, slot: null, ticket: null } : prev,
    );
  };

  const pickSlot = useCallback(
    (slot) => {
      setOrderDetails((prev) => ({
        ...(prev || {}),
        slot,
        // The summary/review read these.
        date: slot.date,
        time: slot.start_time,
        end_date: dayjs(slot.end_at).isAfter(dayjs(slot.start_at), "day")
          ? dayjs(slot.date).add(1, "day").format("YYYY-MM-DD")
          : slot.date,
        end_time: slot.end_time,
        ticket: null,
        subtotal: 0,
        total_amount: 0,
      }));

      const next = getNextStep("slot", availableSteps || []);
      if (next) setActiveStep(next);
    },
    [setOrderDetails, setActiveStep, availableSteps],
  );

  // ---------- date pickers ----------

  const dayButton = (value, { compact = false } = {}) => {
    const d = dayjs(value);
    const info = days[value];
    const past = d.isBefore(today, "day");
    const bookable = !past && isBookable(info);
    const selected = date === value;
    const label = past ? "" : info ? STATUS_LABEL[info.status] || "" : "";
    return (
      <button
        key={value}
        type="button"
        data-date={value}
        disabled={!bookable}
        aria-pressed={selected}
        aria-label={`${d.format("dddd, D MMMM")}${label ? `, ${label}` : ""}`}
        className={[
          compact ? styles.listDay : styles.calDay,
          selected ? styles.daySelected : "",
          info?.status === "limited" ? styles.dayLimited : "",
          info?.status === "sold_out" ? styles.daySoldOut : "",
        ].join(" ")}
        onClick={() => {
          chooseDate(value);
          setCalendarOpen(false);
        }}
      >
        {compact && <span className={styles.listWeekday}>{d.format("ddd")}</span>}
        <span className={styles.dayNumber}>
          {compact
            ? d.format("D MMM")
            : d.date() === 1 || d.isSame(weekStart, "day")
              ? d.format("D MMM")
              : d.date()}
        </span>
        {label && <span className={styles.dayStatus}>{label}</span>}
      </button>
    );
  };

  const calendar = () => {
    const cells = Array.from({ length: GRID_WEEKS * 7 }, (_, i) =>
      weekStart.add(i, "day"),
    );
    const sameYear = weekStart.year() === gridEnd.year();
    return (
      <div className={styles.calendar}>
        <div className={styles.calHead}>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Earlier dates"
            disabled={!weekStart.isAfter(firstWeek)}
            onClick={() => {
              const prev = weekStart.subtract(STEP_WEEKS, "week");
              setWeekStart(prev.isBefore(firstWeek) ? firstWeek : prev);
            }}
          >
            <IconChevronLeft size={18} />
          </button>
          <strong>
            {weekStart.format(sameYear ? "D MMM" : "D MMM YYYY")} –{" "}
            {gridEnd.format("D MMM YYYY")}
          </strong>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Later dates"
            disabled={!weekStart.isBefore(lastWeek)}
            onClick={() => setWeekStart((w) => w.add(STEP_WEEKS, "week"))}
          >
            <IconChevronRight size={18} />
          </button>
        </div>
        <div className={styles.calGrid} role="grid">
          {WEEKDAYS.map((w) => (
            <span key={w} className={styles.calWeekday} role="columnheader">
              {w}
            </span>
          ))}
          {cells.map((d) => dayButton(d.format("YYYY-MM-DD")))}
        </div>
      </div>
    );
  };

  const strip = () => {
    const values = Array.from({ length: STRIP_DAYS }, (_, i) =>
      stripAnchor.add(i, "day").format("YYYY-MM-DD"),
    ).filter((v) => days[v] && days[v].status !== "closed");
    // a day picked further ahead in the calendar joins the strip
    if (date && !values.includes(date)) values.push(date);
    if (!values.length) {
      return loadingDays ? null : (
        <Empty
          description={
            earliestShowDay
              ? "No open dates in this window. Open the full calendar to browse the season."
              : "No open dates in the next 60 days. Open the full calendar to look further ahead."
          }
        />
      );
    }
    const scroll = (dir) =>
      stripRef.current?.scrollBy({
        left: dir * stripRef.current.clientWidth * 0.8,
        behavior: "smooth",
      });
    return (
      <div className={styles.stripWrap}>
        <button
          type="button"
          className={styles.stripArrow}
          aria-label="Earlier dates"
          onClick={() => scroll(-1)}
        >
          <IconChevronLeft size={18} />
        </button>
        <div ref={stripRef} className={styles.strip}>
          {values.map((v) => dayButton(v, { compact: true }))}
        </div>
        <button
          type="button"
          className={styles.stripArrow}
          aria-label="Later dates"
          onClick={() => scroll(1)}
        >
          <IconChevronRight size={18} />
        </button>
      </div>
    );
  };

  // ---------- slots ----------

  const allDay = slots.length === 1;
  const slotList = () => {
    if (loadingSlots) return <Spin />;
    if (slots.length === 0)
      return <Empty description={error || "No entry slots on this day."} />;
    return (
      <div className={allDay ? styles.slotSingle : styles.slotGrid}>
        {slots.map((slot) => {
          const selected = orderDetails?.slot?.start_at === slot.start_at;
          const soldOut = slot.remaining === 0;
          // "few" relative to the slot's size: a slot of 8 isn't low at 8
          const few =
            slot.remaining <=
            Math.max(3, Math.floor((slot.capacity || 0) * 0.2));
          return (
            <button
              key={slot.start_at}
              type="button"
              disabled={!slot.bookable}
              aria-pressed={selected}
              className={`${styles.slot} ${selected ? styles.slotSelected : ""}`}
              onClick={() => slot.bookable && pickSlot(slot)}
            >
              {allDay && <span className={styles.slotKicker}>Entry any time</span>}
              <span className={styles.slotTime}>
                {formatEventTime(slot.start_time)} – {formatEventTime(slot.end_time)}
              </span>
              <span
                className={`${styles.slotState} ${
                  soldOut ? styles.slotSoldOut : few ? styles.slotFew : ""
                }`}
              >
                {soldOut
                  ? "Sold out"
                  : !slot.bookable
                    ? "Not available"
                    : few
                      ? `Only ${slot.remaining} left`
                      : `${slot.remaining} left`}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.wrap}>
      <section>
        <div className={styles.sectionHead}>
          <h3 className={styles.title}>Choose a date</h3>
          <button
            type="button"
            className={styles.calendarButton}
            onClick={() => {
              if (date) {
                const w = mondayOf(dayjs(date));
                setWeekStart(w.isBefore(firstWeek) ? firstWeek : w);
              } else {
                const firstCached = Object.values(days)
                  .filter(isBookable)
                  .sort((a, b) => a.date.localeCompare(b.date))[0];
                if (firstCached?.date) {
                  const w = mondayOf(dayjs(firstCached.date));
                  setWeekStart(w.isBefore(firstWeek) ? firstWeek : w);
                } else if (earliestShowDay) {
                  setWeekStart(mondayOf(earliestShowDay));
                }
              }
              setCalendarOpen(true);
            }}
          >
            <IconCalendarMonth size={16} /> Full calendar
          </button>
        </div>
        <Spin spinning={loadingDays && !calendarOpen}>{strip()}</Spin>
      </section>

      {date && (
        <section>
          <h3 className={styles.title}>
            {allDay ? "Entry" : "Choose a time slot"}
            <span className={styles.titleDate}>
              {dayjs(date).format("dddd, D MMMM")}
            </span>
          </h3>
          {slotList()}
        </section>
      )}

      <Modal
        open={calendarOpen}
        onCancel={() => setCalendarOpen(false)}
        footer={null}
        width={520}
        centered
        title="Pick a date"
      >
        <Spin spinning={loadingDays}>{calendar()}</Spin>
        <div className={styles.legend}>
          <span><i className={styles.dotOpen} /> Available</span>
          <span><i className={styles.dotFew} /> Few left</span>
          <span><i className={styles.dotOff} /> Closed or sold out</span>
        </div>
      </Modal>
    </div>
  );
};

export default SlotStep;
